/**
 * @file services/subscription.js
 * @description Subscription & usage tracking for freemium/premium tiers.
 */

import {
  doc,
  getDoc,
  setDoc,
  increment,
} from "firebase/firestore";
import { db } from "./firebase";

// ── PLAN DEFINITIONS ──────────────────────────────────────────────────────────

export const PLANS = {
  free: {
    name: "Free",
    aiCallsPerDay: 7,
    photoAnalysis: false,
    historyDays: 7,
    exportData: false,
  },
  premium: {
    name: "Premium",
    price: 3.99,
    yearlyPrice: 39.99,
    currency: "USD",
    aiCallsPerDay: Infinity,
    photoAnalysis: true,
    historyDays: Infinity,
    exportData: true,
  },
};

// ── SUBSCRIPTION CRUD ─────────────────────────────────────────────────────────

export async function getSubscription(uid) {
  const ref = doc(db, "users", uid, "config", "subscription");
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    return { plan: "free", expiresAt: null, isActive: false, customerPortalUrl: null };
  }

  const data = snap.data();
  const expiresAt = data.expiresAt?.toDate?.() || null;
  const isActive = data.plan === "premium" && expiresAt && expiresAt > new Date();

  return {
    plan: isActive ? "premium" : "free",
    expiresAt,
    isActive,
    customerPortalUrl: data.customerPortalUrl || null,
  };
}

// ── USAGE TRACKING ────────────────────────────────────────────────────────────

function getTodayKey() {
  return new Date().toISOString().split("T")[0];
}

export async function getUsageToday(uid) {
  const ref = doc(db, "users", uid, "usage", getTodayKey());
  const snap = await getDoc(ref);
  if (!snap.exists()) return { aiCalls: 0, photoCalls: 0 };
  return snap.data();
}

export async function trackAIUsage(uid, isPhoto = false) {
  const ref = doc(db, "users", uid, "usage", getTodayKey());
  const updates = { aiCalls: increment(1) };
  if (isPhoto) updates.photoCalls = increment(1);
  return setDoc(ref, updates, { merge: true });
}

export async function checkAILimit(uid, isPhoto = false) {
  const [sub, usage] = await Promise.all([
    getSubscription(uid),
    getUsageToday(uid),
  ]);

  const plan = PLANS[sub.plan];

  if (isPhoto && !plan.photoAnalysis) {
    return { allowed: false, remaining: 0, plan: sub.plan, reason: "photo_premium" };
  }

  const remaining = plan.aiCallsPerDay - usage.aiCalls;

  if (remaining <= 0) {
    return { allowed: false, remaining: 0, plan: sub.plan, reason: "limit_reached" };
  }

  return { allowed: true, remaining, plan: sub.plan, reason: null };
}